import {
  PublicSnapshotSchema,
  type PublicSnapshot,
} from "@paddock-signal/contracts";

const teams = [
  { id: "northstar", name: "Northstar", color: "#1557FF" },
  { id: "arcline", name: "Arcline", color: "#F0441D" },
  { id: "monument", name: "Monument", color: "#7255D9" },
  { id: "kinetic", name: "Kinetic", color: "#28796B" },
] as const;

const driverRecords = [
  ["mara-voss", "Mara", "Voss", "VOS", "12", "northstar"],
  ["theo-nova", "Theo", "Nova", "NOV", "4", "arcline"],
  ["ines-lane", "Ines", "Lane", "LAN", "23", "monument"],
  ["soren-vale", "Soren", "Vale", "VAL", "8", "kinetic"],
  ["nico-arden", "Nico", "Arden", "ARD", "31", "northstar"],
  ["ami-sato", "Ami", "Sato", "SAT", "17", "arcline"],
  ["elio-marin", "Elio", "Marin", "MAR", "55", "monument"],
  ["jules-reed", "Jules", "Reed", "REE", "6", "kinetic"],
] as const;

const drivers = driverRecords.map(
  ([id, givenName, familyName, acronym, permanentNumber, constructorId]) => ({
    id,
    givenName,
    familyName,
    acronym,
    permanentNumber,
    constructorId,
    accentColor:
      teams.find((team) => team.id === constructorId)?.color ?? "#1557FF",
    active: true,
  }),
);

const locationRecords = [
  ["mara-voss", "Monaco", "MC", 7.4246, 43.7384, 91, "high"],
  ["theo-nova", "Monaco", "MC", 7.429, 43.733, 74, "medium"],
  ["ines-lane", "Nice", "FR", 7.262, 43.7102, 57, "low"],
  ["soren-vale", "London", "GB", -0.1276, 51.5072, 82, "high"],
  ["nico-arden", "Milan", "IT", 9.19, 45.4642, 68, "medium"],
  ["ami-sato", "Tokyo", "JP", 139.6917, 35.6895, 88, "high"],
] as const;

const locations = [
  ...locationRecords.map(
    (
      [
        driverId,
        city,
        countryCode,
        longitude,
        latitude,
        confidenceScore,
        confidenceBand,
      ],
      index,
    ) => ({
      driverId,
      status: "estimated" as const,
      city,
      countryCode,
      coordinates: [longitude, latitude] as [number, number],
      precision: "city" as const,
      confidenceScore,
      confidenceBand,
      observedAt: `2026-08-20T${String(8 + index).padStart(2, "0")}:10:00.000Z`,
      expiresAt: `2026-08-21T${String(8 + index).padStart(2, "0")}:10:00.000Z`,
      evidence: [
        {
          id: `evidence-${driverId}`,
          sourceId: index % 2 === 0 ? "driver-feed" : "press-wire",
          sourceGroupId: `signal-${driverId}`,
          publisher:
            index % 2 === 0
              ? `${drivers[index]?.givenName ?? "Driver"} public feed`
              : "The Track Ledger",
          title:
            index % 2 === 0
              ? "Public city appearance update"
              : "Driver seen at a scheduled media appearance",
          url: `https://example.com/evidence/${driverId}`,
          evidenceType:
            index % 2 === 0
              ? ("driver-self-disclosure" as const)
              : ("reputable-press" as const),
          publishedAt: `2026-08-20T${String(8 + index).padStart(2, "0")}:10:00.000Z`,
          observedAt: `2026-08-20T${String(8 + index).padStart(2, "0")}:10:00.000Z`,
          expiresAt: `2026-08-21T${String(8 + index).padStart(2, "0")}:10:00.000Z`,
          freshness: "fresh" as const,
        },
      ],
      rationale:
        index % 2 === 0
          ? "A permitted direct update places the driver in this city."
          : "A reputable report identifies a current public appearance in this city.",
      expectedNext: {
        label: "Race-weekend media day",
        city: "Montreux",
        countryCode: "CH",
        coordinates: [6.91, 46.4312] as [number, number],
        startsAt: "2026-08-21T09:00:00.000Z",
        endsAt: "2026-08-21T16:00:00.000Z",
        sourceId: "organizer-feed",
        sourceUrl: "https://example.com/schedule/media-day",
      },
    }),
  ),
  ...["elio-marin", "jules-reed"].map((driverId) => ({
    driverId,
    status: "unknown" as const,
    precision: "city" as const,
    evidence: [],
    rationale: "No reliable current estimate is available.",
  })),
];

export const prototypeSnapshot: PublicSnapshot = PublicSnapshotSchema.parse({
  schemaVersion: 1,
  id: "prototype-visual-snapshot",
  generatedAt: "2026-08-20T15:00:00.000Z",
  staleAt: "2026-08-20T17:00:00.000Z",
  season: {
    year: 2026,
    championshipName: "World Drivers Championship",
    rosterUpdatedAt: "2026-08-20T14:45:00.000Z",
  },
  drivers,
  locations,
  weekend: {
    id: "2026-alpine-signal-grand-prix",
    round: 14,
    name: "Alpine Signal Grand Prix",
    circuitId: "circuit-ceramique",
    timezone: "Europe/Zurich",
    startsAt: "2026-08-21T09:00:00.000Z",
    endsAt: "2026-08-23T15:30:00.000Z",
    status: "upcoming",
    sessions: [
      [
        "practice-1",
        "Practice 1",
        "2026-08-21T11:30:00.000Z",
        "2026-08-21T12:30:00.000Z",
      ],
      [
        "practice-2",
        "Practice 2",
        "2026-08-21T15:00:00.000Z",
        "2026-08-21T16:00:00.000Z",
      ],
      [
        "practice-3",
        "Practice 3",
        "2026-08-22T10:30:00.000Z",
        "2026-08-22T11:30:00.000Z",
      ],
      [
        "qualifying",
        "Qualifying",
        "2026-08-22T14:00:00.000Z",
        "2026-08-22T15:00:00.000Z",
      ],
      ["race", "Race", "2026-08-23T13:30:00.000Z", "2026-08-23T15:30:00.000Z"],
    ].map(([kind, label, startsAt, endsAt]) => ({
      id: `alpine-${kind}`,
      kind,
      label,
      startsAt,
      endsAt,
      status: "scheduled",
    })),
  },
  standings: {
    publishedAt: "2026-08-17T17:00:00.000Z",
    sourceId: "jolpica",
    drivers: drivers.map((driver, index) => ({
      position: index + 1,
      driverId: driver.id,
      constructorId: driver.constructorId,
      points: 244 - index * 18,
      wins: Math.max(0, 6 - index),
    })),
    constructors: teams.map((team, index) => ({
      position: index + 1,
      constructorId: team.id,
      name: team.name,
      accentColor: team.color,
      points: 421 - index * 47,
      wins: Math.max(0, 8 - index * 2),
    })),
  },
  progression: {
    publishedAt: "2026-08-17T17:00:00.000Z",
    sourceId: "jolpica",
    rounds: [1, 7, 13].map((round, roundIndex) => ({
      round,
      raceName:
        [
          "Coastal Opening Grand Prix",
          "Midseason Vector Grand Prix",
          "Continental Night Grand Prix",
        ][roundIndex] ?? "Synthetic Grand Prix",
      circuitId: "circuit-ceramique",
      completedAt:
        [
          "2026-03-08T16:00:00.000Z",
          "2026-06-07T16:00:00.000Z",
          "2026-08-16T16:00:00.000Z",
        ][roundIndex] ?? "2026-08-16T16:00:00.000Z",
      drivers: drivers.map((driver, index) => ({
        driverId: driver.id,
        finishPosition: ((index + roundIndex * 3) % drivers.length) + 1,
        racePoints: Math.max(0, 25 - index * 2 - roundIndex),
        sprintPoints: roundIndex === 1 ? Math.max(0, 8 - index) : 0,
        cumulativePoints: Math.max(
          0,
          Math.round(((244 - index * 18) * (roundIndex + 1)) / 3),
        ),
        championshipPosition: index + 1,
      })),
      constructors: teams.map((team, index) => ({
        constructorId: team.id,
        racePoints: Math.max(0, 43 - index * 5 - roundIndex),
        sprintPoints: roundIndex === 1 ? Math.max(0, 13 - index * 2) : 0,
        cumulativePoints: Math.max(
          0,
          Math.round(((421 - index * 47) * (roundIndex + 1)) / 3),
        ),
        championshipPosition: index + 1,
      })),
    })),
  },
  circuits: [
    {
      id: "circuit-ceramique",
      round: 14,
      name: "Circuit Céramique",
      city: "Montreux",
      countryCode: "CH",
      coordinates: [6.91, 46.4312],
      status: "current",
      assetState: "3d",
      geometryAttribution: "Synthetic M2 fixture geometry",
      elevationAttribution: "Synthetic M2 fixture elevation",
    },
  ],
  sourceHealth: [
    {
      sourceId: "jolpica",
      status: "healthy",
      checkedAt: "2026-08-20T15:00:00.000Z",
      lastSuccessfulAt: "2026-08-20T15:00:00.000Z",
    },
    {
      sourceId: "driver-feed",
      status: "healthy",
      checkedAt: "2026-08-20T15:00:00.000Z",
      lastSuccessfulAt: "2026-08-20T14:57:00.000Z",
    },
    {
      sourceId: "press-wire",
      status: "healthy",
      checkedAt: "2026-08-20T15:00:00.000Z",
      lastSuccessfulAt: "2026-08-20T14:52:00.000Z",
    },
    {
      sourceId: "organizer-feed",
      status: "healthy",
      checkedAt: "2026-08-20T15:00:00.000Z",
      lastSuccessfulAt: "2026-08-20T14:50:00.000Z",
    },
  ],
});

export const teamNames = Object.fromEntries(
  teams.map((team) => [team.id, team.name]),
);
